import { useState } from "react";
import api from "@/lib/api";
import { t } from "@/i18n";
import { formatEnum, formatMetricChange } from "@/utils/formatEnum";
import {
  Sparkles, Target, TrendingDown, TrendingUp, Info, AlertTriangle,
  CheckCircle2, XCircle, Clock, RefreshCw, ShieldQuestion,
} from "lucide-react";

const EVIDENCE_STYLE = {
  MEASURED:                  { cls: "bg-pos-green/10 text-pos-green border-pos-green/40", tip: "Direct gemeten uitkomst" },
  EXPERIMENTALLY_SUPPORTED:  { cls: "bg-info-blue/10 text-info-blue border-info-blue/40", tip: "Ondersteund door een experiment" },
  MODELLED_ESTIMATE:         { cls: "bg-warn-amber/10 text-warn-amber border-warn-amber/40", tip: "Geschat met een rekenmodel" },
  DIRECTIONAL_ESTIMATE:      { cls: "bg-warn-amber/10 text-warn-amber border-warn-amber/40", tip: "Globale inschatting zonder harde meting" },
  UNKNOWN:                   { cls: "bg-bg-panel-raised text-text-muted border-border-hairline", tip: "Nog onbekend" },
};

const STATUS_STYLE = {
  ACTIVE:      "bg-info-blue/10 text-info-blue border-info-blue/40",
  RECONSIDER:  "bg-warn-amber/10 text-warn-amber border-warn-amber/40",
  EXPIRED:     "bg-bg-panel-raised text-text-muted border-border-hairline",
  WITHDRAWN:   "bg-bg-panel-raised text-text-muted border-border-hairline",
};

const RELIABILITY_STYLE = {
  PROVEN:            "bg-pos-green/10 text-pos-green border-pos-green/40",
  BUILDING_EVIDENCE: "bg-info-blue/10 text-info-blue border-info-blue/40",
  INSUFFICIENT_DATA: "bg-bg-panel-raised text-text-muted border-border-hairline",
};

const fmtEUR = (n) => (n == null ? "—" : `€ ${Number(n).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("nl-NL", { maximumFractionDigits: 2 }));
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export function DecisionCard({ card, compact = false, onReloadParent }) {
  const [showRealityModal, setShowRealityModal] = useState(false);
  if (!card) return null;

  if (!card.has_full_decision_metadata) {
    return (
      <div className="rounded-lg border border-border-hairline bg-bg-panel p-5" data-testid="decision-card-fallback">
        <div className="flex items-center gap-2 mb-2">
          <Info className="w-4 h-4 text-text-muted" />
          <span className="text-[10px] text-text-muted font-semibold">
            {t("decisionTwin.fallback_badge")}
          </span>
        </div>
        <div className="text-sm font-semibold text-text-primary">{card.title}</div>
        <div className="text-xs text-text-muted mt-1">{card.advice_nl || card.why_nl || "—"}</div>
        <div className="text-xs text-text-muted mt-2 italic">{t("decisionTwin.fallback_note")}</div>
      </div>
    );
  }

  const ev = EVIDENCE_STYLE[card.evidence_class] || EVIDENCE_STYLE.UNKNOWN;
  const statusCls = STATUS_STYLE[card.computed_status] || STATUS_STYLE.ACTIVE;

  return (
    <div className="rounded-lg border border-border-hairline bg-bg-panel p-5" data-testid="decision-card">
      {/* Header: title + status + evidence class */}
      <div className="flex items-start gap-3 flex-wrap mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] px-2 py-0.5 rounded border font-bold ${statusCls}`}
                   data-testid="decision-card-status">
              {t(`enum.status.${card.computed_status}`)}
            </span>
            <span
              className={`text-[10px] px-2 py-0.5 rounded border font-bold ${ev.cls}`}
              title={ev.tip}
              data-testid="decision-card-evidence-class"
            >
              {t(`enum.evidence.${card.evidence_class || "UNKNOWN"}`)}
            </span>
            <span className="text-[10px] text-text-muted">
              {formatEnum("action_type", card.action_type, card.action_type)}
            </span>
          </div>
          <h3 className="text-lg font-display font-bold text-text-primary tracking-tight" data-testid="decision-card-title">
            {card.title}
          </h3>
        </div>
      </div>

      {/* Advies + Waarom */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <Section icon={Sparkles} label={t("decisionTwin.advice")} testid="dc-advice">
          <p className="text-sm text-text-primary leading-relaxed">{card.advice_nl || "—"}</p>
        </Section>
        <Section icon={Info} label={t("decisionTwin.why")} testid="dc-why">
          <p className="text-sm text-text-primary leading-relaxed">{card.why_nl || "—"}</p>
        </Section>
      </div>

      {/* Baseline + Twee scenario's */}
      <div className={`grid ${compact ? "md:grid-cols-3" : "md:grid-cols-3"} gap-4 mb-4`}>
        <Section icon={Target} label={t("decisionTwin.baseline")} testid="dc-baseline">
          {card.baseline
            ? <MetricList m={card.baseline} />
            : <p className="text-xs text-text-muted italic">{t("decisionTwin.baseline_missing")}</p>}
        </Section>
        <Section icon={TrendingDown} label={t("decisionTwin.no_change")} testid="dc-no-change">
          {card.no_change_projection
            ? <MetricList m={card.no_change_projection} />
            : (
              <div className="text-xs text-text-muted leading-relaxed">
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-warn-amber/10 text-warn-amber border border-warn-amber/40 mr-1">
                  {t("common.hypothesis_label")}
                </span>
                {card.action_type === "NO_ACTION"
                  ? t("decisionTwin.no_change_is_advice")
                  : t("decisionTwin.no_change_unknown")}
              </div>
            )}
        </Section>
        <Section icon={TrendingUp} label={t("decisionTwin.follow_advice")} testid="dc-follow">
          {card.expected_metrics_range ? (
            <div className="text-xs text-text-primary leading-relaxed space-y-1">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-warn-amber/10 text-warn-amber border border-warn-amber/40 mr-1">
                {t("common.hypothesis_label")}
              </span>
              <div className="mt-1 text-text-primary">
                {formatMetricChange(
                  card.expected_metrics_range.direction,
                  card.expected_metrics_range.metric,
                  card.expected_metrics_range.delta_low_pct,
                  card.expected_metrics_range.delta_high_pct
                )}
              </div>
              {card.expected_return_low_eur != null && (
                <div className="text-text-muted">
                  {fmtEUR(card.expected_return_low_eur)} – {fmtEUR(card.expected_return_high_eur)}
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-text-muted italic">{t("decisionTwin.follow_missing")}</p>
          )}
        </Section>
      </div>

      {/* Confidence bar + Risk + Upside */}
      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <ConfidenceBar testid="dc-confidence"
                        value={card.confidence != null ? Math.round((card.confidence || 0) * 100) : null}
                        reason={card.confidence_reason_nl} />
        <MiniStat testid="dc-risk" label={t("decisionTwin.risk")}
                   value={card.risk_nl ? card.risk_nl.split(/[—.]/)[0].trim() : "—"}
                   reason={card.risk_nl} />
        <MiniStat testid="dc-upside" label={t("decisionTwin.expected_upside")}
                   value={card.expected_return_low_eur != null
                     ? `${fmtEUR(card.expected_return_low_eur)} – ${fmtEUR(card.expected_return_high_eur)}`
                     : (card.expected_upside_nl || "—")}
                   reason={t("common.hypothesis_label")} />
      </div>

      {/* Evidence class disclaimer */}
      {["MODELLED_ESTIMATE", "DIRECTIONAL_ESTIMATE", "UNKNOWN"].includes(card.evidence_class) && (
        <div className="mb-4 p-2.5 rounded-md border border-warn-amber/40 bg-warn-amber/10 text-warn-amber text-[11px] flex items-start gap-2"
             data-testid="dc-evidence-disclaimer">
          <ShieldQuestion className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            {(() => {
              const nl = t(`enum.evidence.${card.evidence_class || "UNKNOWN"}`);
              const label = nl && !nl.startsWith("enum.") ? nl : (card.evidence_class || "onbekend");
              // Template uses `{class}` as a verb-phrase already inflected in
              // the enum-label (lower-cased, includes article where needed).
              return t("decisionTwin.evidence_disclaimer").replace("{class}", label);
            })()}
          </span>
        </div>
      )}

      {/* Reconsider triggers */}
      <div className="mb-4">
        <div className="text-[10px] text-text-muted font-semibold mb-2 flex items-center gap-1.5">
          <RefreshCw className="w-3 h-3" /> {t("decisionTwin.reconsider_triggers")}
        </div>
        {card.reconsider_triggers && card.reconsider_triggers.length > 0 ? (
          <ul className="space-y-1.5" data-testid="dc-reconsider-list">
            {card.reconsider_triggers.map((tr, i) => (
              <li key={i} data-testid={`dc-reconsider-${i}`}
                   className={`text-xs px-3 py-1.5 rounded border ${tr.hit ? "bg-warn-amber/10 border-warn-amber/40 text-warn-amber" : "bg-bg-panel border-border-hairline text-text-primary/85"}`}>
                <span className="font-mono">{tr.metric} {tr.comparator} {fmtNum(tr.threshold)}</span>
                {tr.window_days && <span className="text-text-muted"> · {tr.window_days}d</span>}
                {tr.description_nl && <span className="ml-1">— {tr.description_nl}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-text-muted italic" data-testid="dc-reconsider-empty">
            {t("decisionTwin.reconsider_empty").replace("{date}", fmtDate(card.review_at))}
          </p>
        )}
      </div>

      {/* Timings */}
      <div className="flex items-center gap-4 flex-wrap text-[11px] text-text-muted pt-3 border-t border-border-hairline">
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />
          {t("decisionTwin.review_at")}: <b className="text-text-primary/85">{fmtDate(card.review_at)}</b>
        </span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />
          {t("decisionTwin.expires_at")}: <b className="text-text-primary/85">{fmtDate(card.expires_at)}</b>
        </span>
        {card.last_revalidated_at && (
          <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />
            {t("decisionTwin.last_revalidated_at")}: <b className="text-text-primary/85">{fmtDate(card.last_revalidated_at)}</b>
          </span>
        )}
      </div>

      {compact ? null : (
        <>
          {/* Reality vs JustEasySEO columns */}
          <RealityNexoraColumns card={card} onOpen={() => setShowRealityModal(true)} />
          {/* Similar decisions */}
          <SimilarDecisions items={card.similar_decisions} />
          {/* Reliability */}
          <ReliabilityBlock rel={card.reliability} />
        </>
      )}

      {showRealityModal && (
        <RealityModal
          card={card}
          onClose={() => setShowRealityModal(false)}
          onSaved={() => { setShowRealityModal(false); onReloadParent && onReloadParent(); }}
        />
      )}
    </div>
  );
}

function Section({ icon: Icon, label, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] text-text-muted font-semibold mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      {children}
    </div>
  );
}

function ConfidenceBar({ value, reason, testid }) {
  const pct = value != null ? Math.max(0, Math.min(100, Number(value))) : null;
  return (
    <div data-testid={testid}
          className="min-h-[74px] rounded-md border border-border-hairline bg-bg-panel-raised p-3 flex flex-col justify-between">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-text-muted"
              style={{ letterSpacing: "0.08em" }}>
          {t("decisionTwin.confidence")}
        </span>
        <span className="text-sm text-text-primary num">
          {pct != null ? `${pct}%` : "—"}
        </span>
      </div>
      <div className="mt-2 w-full h-1 bg-bg-canvas rounded-[2px] overflow-hidden">
        <div
          className="h-full bg-accent-teal transition-all duration-exec"
          style={{ width: pct != null ? `${pct}%` : "0%" }}
        />
      </div>
      {reason && (
        <div className="mt-2 text-[11px] text-text-muted italic">{reason}</div>
      )}
    </div>
  );
}

function MiniStat({ label, value, reason, testid }) {
  return (
    <div data-testid={testid} className="rounded-md border border-border-hairline bg-bg-panel p-3">
      <div className="text-[9px] text-text-muted font-semibold mb-1">{label}</div>
      <div className="text-sm font-semibold text-text-primary">{value}</div>
      {reason && <div className="text-[11px] text-text-muted mt-1 leading-tight">{reason}</div>}
    </div>
  );
}

function MetricList({ m }) {
  const metricLabels = {
    spend_eur: "Uitgaven (€)",
    conversions: "Conversies",
    clicks: "Klikken",
    impressions: "Vertoningen",
    cpa: "Kosten per aanvraag",
    roas: "Opbrengst per euro",
    ctr: "Doorklikratio",
    cvr: "Conversieratio",
  };
  const keys = ["spend_eur", "conversions", "clicks", "impressions"];
  return (
    <dl className="text-xs text-text-primary space-y-0.5 font-mono tabular-nums">
      {keys.filter((k) => m[k] != null).map((k) => (
        <div key={k} className="flex justify-between gap-3">
          <dt className="text-text-muted">{metricLabels[k] || k}</dt>
          <dd>{k === "spend_eur" ? fmtEUR(m[k]) : fmtNum(m[k])}</dd>
        </div>
      ))}
    </dl>
  );
}

function RealityNexoraColumns({ card, onOpen }) {
  const reality = card.reality || {};
  const nexora = card.nexora || {};
  const unreliable = ["UNKNOWN", "DIRECTIONAL_ESTIMATE"].includes(card.evidence_class);
  return (
    <div className="mt-5 pt-4 border-t border-border-hairline" data-testid="dc-reality-nexora">
      <div className="text-[10px] text-text-muted font-semibold mb-3">
        {t("decisionTwin.reality_vs_nexora")}
      </div>
      {unreliable && (
        <div className="mb-3 p-2.5 rounded-md border border-warn-amber/40 bg-warn-amber/10 text-warn-amber text-[11px] flex items-start gap-2"
             data-testid="dc-comparison-unreliable">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{(() => {
              const nl = t(`enum.evidence.${card.evidence_class || "UNKNOWN"}`);
              const label = nl && !nl.startsWith("enum.") ? nl : (card.evidence_class || "onbekend");
              return t("decisionTwin.comparison_unreliable").replace("{class}", label.toLowerCase());
            })()}</span>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-md border border-border-hairline bg-bg-panel p-3" data-testid="dc-column-reality">
          <div className="text-[10px] text-text-muted font-semibold mb-2">
            {t("decisionTwin.reality")}
          </div>
          <div className="text-xs text-text-primary space-y-1">
            <div><b>{t("decisionTwin.user_action_taken")}:</b>{" "}
              <span className="font-mono">{reality.user_action_taken || "PENDING"}</span></div>
            {reality.user_action_taken_at && (
              <div className="text-text-muted">{fmtDate(reality.user_action_taken_at)}</div>
            )}
            {reality.user_action_note && (
              <div className="text-text-muted italic">"{reality.user_action_note}"</div>
            )}
            {reality.reality_after_snapshot ? (
              <div className="mt-2">
                <div className="text-[9px] text-text-muted mb-1">
                  {t("decisionTwin.reality_after")}
                </div>
                <MetricList m={reality.reality_after_snapshot} />
              </div>
            ) : (
              <div className="text-[11px] text-text-muted italic mt-2" data-testid="dc-reality-no-measurement">
                {t("decisionTwin.reality_no_measurement")}
              </div>
            )}
            {(!reality.user_action_taken || reality.user_action_taken === "PENDING") && (
              <button onClick={onOpen}
                       data-testid="dc-open-reality-modal"
                       className="mt-3 h-8 px-3 rounded-btn bg-accent-teal hover:bg-accent-teal-dim text-white text-xs font-semibold">
                {t("decisionTwin.what_did_you_do")}
              </button>
            )}
          </div>
        </div>
        <div className="rounded-md border border-border-hairline bg-bg-panel p-3" data-testid="dc-column-nexora">
          <div className="text-[10px] text-text-muted font-semibold mb-2">
            {t("decisionTwin.nexora_path")}
          </div>
          <div className="text-xs text-text-primary space-y-1">
            <div><b>{t("decisionTwin.advice")}:</b> {nexora.advice_at_decision_time || "—"}</div>
            {nexora.expected_metrics_range && (
              <div className="text-text-primary/85">
                {formatMetricChange(
                  nexora.expected_metrics_range.direction,
                  nexora.expected_metrics_range.metric,
                  nexora.expected_metrics_range.delta_low_pct,
                  nexora.expected_metrics_range.delta_high_pct
                )}
              </div>
            )}
            <div>{t("decisionTwin.confidence")}: <b>{Math.round((nexora.confidence_at_decision || 0) * 100)}%</b></div>
            <div>{t("decisionTwin.evidence_class")}: <b>{nexora.evidence_class_at_decision || "—"}</b></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SimilarDecisions({ items }) {
  return (
    <div className="mt-5 pt-4 border-t border-border-hairline" data-testid="dc-similar">
      <div className="text-[10px] text-text-muted font-semibold mb-2">
        {t("decisionTwin.similar_decisions")}
      </div>
      {items && items.length > 0 ? (
        <ul className="space-y-1.5">
          {items.map((s) => (
            <li key={s.ghost_action_id} data-testid={`dc-similar-${s.ghost_action_id}`}
                 className="flex items-center gap-2 flex-wrap text-xs">
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-bg-panel-raised text-text-primary/85 border border-border-hairline"
                    title={s.action_type}>
                {t(`enum.action_type.${s.action_type}`) && !t(`enum.action_type.${s.action_type}`).startsWith("enum.") ? t(`enum.action_type.${s.action_type}`) : s.action_type}
              </span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded ${ s.outcome === "VALIDATED_LATER" ? "bg-pos-green/20 text-pos-green" : s.outcome === "INVALIDATED_LATER" ? "bg-neg-red/20 text-neg-red" : "bg-bg-panel-raised text-text-muted"}`}>
                {t(`enum.outcome.${s.outcome || "PROPOSED"}`)}
              </span>
              <span className="text-text-primary/85 truncate">{s.title}</span>
              <span className="text-text-subtle ml-auto">sim {Math.round((s.similarity || 0) * 100)}%</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-text-muted italic" data-testid="dc-similar-empty">
          {t("decisionTwin.similar_empty")}
        </p>
      )}
    </div>
  );
}

function ReliabilityBlock({ rel }) {
  if (!rel) return null;
  const cls = RELIABILITY_STYLE[rel.reliability_label] || RELIABILITY_STYLE.INSUFFICIENT_DATA;
  return (
    <div className="mt-5 pt-4 border-t border-border-hairline" data-testid="dc-reliability">
      <div className="text-[10px] text-text-muted font-semibold mb-2">
        {t("decisionTwin.reliability")}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`text-[10px] px-2 py-0.5 rounded border font-bold ${cls}`}
               data-testid="dc-reliability-label">
          {rel.reliability_label}
        </span>
        <span className="text-xs text-text-muted">
          {rel.validated}V · {rel.invalidated}I · {rel.inconclusive}? / {rel.total}
        </span>
      </div>
      <p className="text-xs text-text-primary/85 mt-2 leading-relaxed" data-testid="dc-reliability-hint">
        {rel.business_specific_confidence_hint}
      </p>
    </div>
  );
}

function RealityModal({ card, onClose, onSaved }) {
  const [action, setAction] = useState(card?.reality?.user_action_taken || "PENDING");
  const [note, setNote] = useState(card?.reality?.user_action_note || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const OPTIONS = [
    { v: "FOLLOWED_ADVICE", label: t("decisionTwin.action_followed") },
    { v: "PARTIALLY_FOLLOWED", label: t("decisionTwin.action_partial") },
    { v: "DIFFERENT_ACTION", label: t("decisionTwin.action_different") },
    { v: "NO_ACTION", label: t("decisionTwin.action_none") },
    { v: "PENDING", label: t("decisionTwin.action_pending") },
  ];

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post(`/ghost-ledger/${card.ghost_action_id}/reality`,
                     { user_action_taken: action, note: note || null });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || t("common.error_generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="dialog" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          data-testid="dc-reality-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-card bg-bg-panel p-5 shadow-popover" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-display font-bold text-text-primary mb-3">
          {t("decisionTwin.what_did_you_do")}
        </h3>
        <div className="space-y-2 mb-4">
          {OPTIONS.map((o) => (
            <label key={o.v} className="flex items-start gap-2 p-2 rounded border border-border-hairline hover:bg-bg-panel cursor-pointer"
                    data-testid={`dc-action-option-${o.v}`}>
              <input type="radio" name="action" value={o.v}
                      checked={action === o.v} onChange={() => setAction(o.v)} className="mt-1" />
              <span className="text-sm">{o.label}</span>
            </label>
          ))}
        </div>
        <label className="block text-xs text-text-muted mb-1">{t("decisionTwin.note_optional")}</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)}
                    data-testid="dc-action-note"
                    className="w-full min-h-[80px] rounded-btn border border-border-hairline p-2 text-sm" />
        {err && <div className="mt-2 text-xs text-neg-red">{err}</div>}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3 rounded-btn text-sm text-text-muted hover:text-text-primary"
                   data-testid="dc-action-cancel">
            {t("common.cancel")}
          </button>
          <button onClick={save} disabled={busy}
                   data-testid="dc-action-save"
                   className="h-9 px-4 rounded-btn bg-accent-teal hover:bg-accent-teal-dim text-white text-sm font-semibold disabled:opacity-50">
            {busy ? "…" : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DecisionCard;
